// src/pages/Location.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Calendar as CalendarIcon,
  Users,
  Car,
  Settings,
  Fuel,
  BadgePercent,
  Send,
  User,
  Mail,
  Phone,
  Home,
  MessageSquare,
  Wallet,
  X,
  Eye,
  CheckCircle2,
  Search,
  Filter,
} from "lucide-react";
import toast from "react-hot-toast";
import { notifyApiError } from "../utils/apiError";
import { useAuth } from "../contexts/AuthContext";
import carRentalService from "../services/carRentalService";
import cmiService from "../services/cmiService";
import { getPaymentMode } from "../services/settingsService";
import useSpeechToText from "../hooks/useSpeechToText";
import VoiceDictation from "../components/VoiceDictation";
import useMicLevel from "../hooks/useMicLevel";
import zelijPattern from "../assets/zelij3.png";

// ✅ Use translation function (same fix style)
import { t as tr } from "../contexts/translations";

/* =========================
   API BASE (no hardcoded URLs)
========================= */
const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.VITE_API_BASE_URL ||
  "https://nouzhaback-theta.vercel.app";

// ---------- Utils dates ----------
const getToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split("T")[0];
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const formatMAD = (n) => {
  const x = Number(n);
  if (Number.isNaN(x)) return "";
  return new Intl.NumberFormat("fr-MA").format(x) + " MAD";
};

const safeImg = (url) => {
  if (!url) return "";
  // allow absolute or relative
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  // common: "/uploads/.."
  if (url.startsWith("/")) return `${API_BASE}${url}`;
  // otherwise treat as relative path
  return `${API_BASE}/${url}`;
};

const ModalShell = ({ open, onClose, title, children, maxWidth = "max-w-3xl" }) => {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => {
            // click outside
            if (e.target === e.currentTarget) onClose?.();
          }}
        >
          {/* overlay */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

          {/* center container */}
          <div className="relative min-h-full flex items-center justify-center px-4 py-8">
            <motion.div
              className={`relative w-full ${maxWidth} bg-white rounded-3xl shadow-2xl border border-orange-100 overflow-hidden max-h-[90vh] flex flex-col`}
              initial={{ y: 18, scale: 0.98, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 14, scale: 0.98, opacity: 0 }}
              transition={{ duration: 0.22 }}
            >
              {/* header fixed */}
              <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
                <div className="text-lg md:text-xl font-bold text-gray-800">{title}</div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 rounded-xl hover:bg-gray-100 transition"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-gray-700" />
                </button>
              </div>

              {/* body scrollable */}
              <div className="p-6 overflow-y-auto">{children}</div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Location = ({ language }) => {
  const { currentUser } = useAuth();
  const today = useMemo(() => getToday(), []);

  // ✅ helper translation with fallback
  const T = (key, fallback) => {
    const v = tr(key, language);
    return v === key ? fallback : v;
  };

  // =========================
  // Mode selector
  // =========================
  const [mode, setMode] = useState("form"); // "form" | "catalog"

  // =========================
  // Speech-to-Text (only for form mode)
  // =========================
  const stt = useSpeechToText({
    lang: language === "ar" ? "ar-MA" : language === "en" ? "en-US" : "fr-FR",
    continuous: true,
    interimResults: true,
  });

  useMicLevel(stt.listening);

  useEffect(() => {
    if (!stt.supported) return;
    const full = (stt.transcript + (stt.interim ? " " + stt.interim : "")).trim();
    if (full) console.log("[STT Location] =>", full);
  }, [stt.supported, stt.transcript, stt.interim]);

  // =========================
  // FORM states (demande sur mesure)
  // =========================
  const [pickupLocation, setPickupLocation] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [returnDate, setReturnDate] = useState("");

  // Pré-remplissage depuis les dates choisies dans le hero (?pickupDate&returnDate).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const p = searchParams.get("pickupDate");
    const r = searchParams.get("returnDate");
    if (p) setPickupDate(p);
    if (r) setReturnDate(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [nbPersonnes, setNbPersonnes] = useState(1);

  const [carType, setCarType] = useState("");
  const [transmission, setTransmission] = useState("");
  const [fuelType, setFuelType] = useState("");

  const [prixMin, setPrixMin] = useState(200);
  const [prixMax, setPrixMax] = useState(2000);

  const [souhaiteReduction, setSouhaiteReduction] = useState(false);
  const [comments, setComments] = useState("");

  // ---------- Personal info ----------
  const [nomComplet, setNomComplet] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [adresse, setAdresse] = useState("");

  const [acceptCgv, setAcceptCgv] = useState(false);
  const [formStep, setFormStep] = useState(0);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const applyLocationFields = (fields) => {
    if (!fields) return;

    if (fields.pickupLocation) setPickupLocation(String(fields.pickupLocation).trim());
    if (fields.dateDebut) setPickupDate(fields.dateDebut);
    if (fields.dateFin) setReturnDate(fields.dateFin);

    if (fields.nbPersonnes != null) setNbPersonnes(String(fields.nbPersonnes));

    if (fields.preferences?.carType != null) setCarType(fields.preferences.carType || "");
    if (fields.preferences?.transmission != null) setTransmission(fields.preferences.transmission || "");
    if (fields.preferences?.fuelType != null) setFuelType(fields.preferences.fuelType || "");

    if (fields.budgetParJour?.min != null) setPrixMin(Number(fields.budgetParJour.min));
    if (fields.budgetParJour?.max != null) setPrixMax(Number(fields.budgetParJour.max));

    if (typeof fields.souhaiteReduction === "boolean") setSouhaiteReduction(fields.souhaiteReduction);

    if (typeof fields.comments === "string" && fields.comments.trim()) setComments(fields.comments);

    if (fields.client?.nomComplet) setNomComplet(fields.client.nomComplet);
    if (fields.client?.email && !currentUser) setEmail(fields.client.email);
    if (fields.client?.telephone) setTelephone(fields.client.telephone);
    if (fields.client?.adresse) setAdresse(fields.client.adresse);
  };

  const handleGeminiAutofill = async () => {
    const spoken = (stt.transcript + (stt.interim ? " " + stt.interim : "")).trim();
    if (!spoken) return toast.error(T("locationSpeakFirst", "Parle d'abord, puis clique sur Autofill."));

    try {
      const res = await fetch(`${API_BASE}/api/ai/parse-location`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: spoken, lang: language }),
      });

      const data = await res.json();
      if (!data?.success) {
        toast.error(data?.message || T("geminiError", "Erreur Gemini"));
        return;
      }

      applyLocationFields(data.fields);
      toast.success(T("geminiAutofillSuccess", "Formulaire location rempli à partir de ta voix ✅"));
    } catch (e) {
      console.error(e);
      toast.error(T("geminiNetworkError", "Erreur réseau / serveur Gemini"));
    }
  };

  useEffect(() => {
    setEmail(currentUser?.email || "");
  }, [currentUser]);

  const validateFormDemande = () => {
    if (!pickupLocation.trim()) {
      toast.error(T("vPickupLocation", "Veuillez saisir le lieu de prise en charge."));
      return false;
    }
    if (!pickupDate) {
      toast.error(T("vStartDate", "Veuillez choisir la date de début."));
      return false;
    }
    if (!returnDate) {
      toast.error(T("vEndDate", "Veuillez choisir la date de retour."));
      return false;
    }

    const a = new Date(pickupDate);
    const b = new Date(returnDate);
    if (a >= b) {
      toast.error(T("vEndAfterStart", "La date de retour doit être après la date de début."));
      return false;
    }

    if (!nomComplet.trim()) {
      toast.error(T("vFullName", "Veuillez saisir votre nom complet."));
      return false;
    }
    if (!email.trim()) {
      toast.error(T("vEmail", "Veuillez saisir votre email."));
      return false;
    }
    if (!telephone.trim()) {
      toast.error(T("vPhone", "Veuillez saisir votre téléphone."));
      return false;
    }
    if (!adresse.trim()) {
      toast.error(T("vAddress", "Veuillez saisir votre adresse."));
      return false;
    }

    if (prixMin < 0 || prixMax < 0 || prixMin > prixMax) {
      toast.error(T("vBudgetRange", "Veuillez vérifier l’intervalle de budget (min/max)."));
      return false;
    }

    if (!acceptCgv) {
      toast.error(T("vAcceptCgv", "Veuillez accepter les conditions de paiement (CMI/CGV)."));
      return false;
    }

    return true;
  };

  const handleSubmitDemande = async (e) => {
    e.preventDefault();
    // Garde-fou wizard : avancer tant qu'on n'est pas à la dernière étape
    if (formStep !== lastFormStep) { formNext(); return; }
    if (!validateFormDemande()) return;

    const payload = {
  pickupLocation: pickupLocation.trim(),
  dateDebut: pickupDate,
  dateFin: returnDate,
  nbPersonnes: Number(nbPersonnes) || 1,

  // FORM mode: omit requestedCarId entirely (backend validates it as MongoId when present)
  requestedCarId: undefined,

  preferences: {
    carType: carType || null,
    transmission: transmission || null,
    fuelType: fuelType || null,
  },

  budgetParJour: {
    min: Number(prixMin) || 0,
    max: Number(prixMax) || 0,
  },

  souhaiteReduction: !!souhaiteReduction,
  comments: comments || "",

  client: {
    nomComplet: nomComplet.trim(),
    email: String(currentUser?.email || email || "").trim(),
    telephone: telephone.trim(),
    adresse: adresse.trim(),
  },

  acceptCgv: !!acceptCgv,
  statut: "EN_ATTENTE",
  compteId: currentUser?._id || null,
  lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
  serviceType: "VOITURE_LOCATION",
};


    try {
      setLoading(true);

      // ✅ doit pointer sur POST /demande côté backend (dans ton service)
      const res = await carRentalService.createRequest(payload);

      if (res?.success === false) {
        toast.error(res?.message || T("sendError", "Erreur lors de l’envoi. Veuillez réessayer."));
        return;
      }

      toast.success(res?.message || T("locationSent", "Votre demande de location a été envoyée !"));
      setSuccess(true);

      // reset
      setPickupLocation("");
      setPickupDate("");
      setReturnDate("");
      setNbPersonnes(1);
      setCarType("");
      setTransmission("");
      setFuelType("");
      setPrixMin(200);
      setPrixMax(2000);
      setSouhaiteReduction(false);
      setComments("");

      setNomComplet("");
      setTelephone("");
      setAdresse("");
      setAcceptCgv(false);
      if (!currentUser) setEmail("");
    } catch (err) {
      console.error("DEMANDE LOCATION VOITURE ERROR:", err?.response?.data || err);
      notifyApiError(err, T("sendError", "Erreur lors de l’envoi. Veuillez réessayer."));
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // CATALOG states (choisir une voiture existante)
  // =========================
  const [cars, setCars] = useState([]);
  const [carsLoading, setCarsLoading] = useState(false);
  const [carsError, setCarsError] = useState("");

  const [q, setQ] = useState("");
  const [filterVille, setFilterVille] = useState("");
  const [filterType, setFilterType] = useState("");
  const [onlyDisponible, setOnlyDisponible] = useState(true);
  const [priceMinCat, setPriceMinCat] = useState(0);
  const [priceMaxCat, setPriceMaxCat] = useState(5000);

  const villes = useMemo(() => {
    const set = new Set((cars || []).map((c) => (c?.ville || "").trim()).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [cars]);

  const typesVoiture = useMemo(() => {
    const set = new Set((cars || []).map((c) => (c?.type_voiture || "").trim()).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [cars]);

  const fetchCars = async () => {
    try {
      setCarsLoading(true);
      setCarsError("");
      const res = await fetch(`${API_BASE}/api/voitures`);
      const data = await res.json();

      // Accept either {success, data} or plain array
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.voitures)
        ? data.voitures
        : [];
      setCars(list);

      // auto-range
      const prices = list.map((c) => Number(c?.prix_jour)).filter((x) => !Number.isNaN(x));
      if (prices.length) {
        const mn = Math.max(0, Math.floor(Math.min(...prices)));
        const mx = Math.ceil(Math.max(...prices));
        setPriceMinCat(mn);
        setPriceMaxCat(mx);
      }
    } catch (e) {
      console.error(e);
      setCarsError(T("carsFetchError", "Impossible de charger les voitures. Vérifie le serveur /api/voitures."));
    } finally {
      setCarsLoading(false);
    }
  };

  useEffect(() => {
    if (mode !== "catalog") return;
    fetchCars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // === Filtrage réel par dates (catalogue) ===
  // Périodes réservées par voiture (chargées à la demande) : { [carId]: [{dateDebut,dateFin,statut}] }.
  const [reservedByCar, setReservedByCar] = useState({});
  // Le filtre par dates n'est actif qu'en catalogue avec une plage complète choisie.
  const datesActive = mode === "catalog" && !!pickupDate && !!returnDate;

  // Charge les périodes réservées des voitures affichées quand une plage est sélectionnée.
  useEffect(() => {
    if (!datesActive || !Array.isArray(cars) || cars.length === 0) return;
    const missing = cars.filter((c) => c?._id && reservedByCar[c._id] === undefined);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        missing.map(async (c) => {
          try {
            const res = await carRentalService.getCarReservedPeriods(c._id);
            return [c._id, res?.data?.periodesReservees || []];
          } catch {
            return [c._id, []]; // en cas d'erreur : ne pas masquer la voiture
          }
        })
      );
      if (cancelled) return;
      setReservedByCar((prev) => {
        const next = { ...prev };
        results.forEach(([id, periods]) => {
          next[id] = periods;
        });
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [datesActive, cars, reservedByCar]);

  const filteredCars = useMemo(() => {
    const query = (q || "").toLowerCase().trim();
    const inStart = datesActive ? new Date(pickupDate) : null;
    const inEnd = datesActive ? new Date(returnDate) : null;

    return (cars || []).filter((c) => {
      if (!c) return false;

      if (onlyDisponible && c.disponible === false) return false;

      if (filterVille && String(c.ville || "").toLowerCase() !== String(filterVille).toLowerCase()) return false;
      if (filterType && String(c.type_voiture || "").toLowerCase() !== String(filterType).toLowerCase()) return false;

      const p = Number(c.prix_jour);
      if (!Number.isNaN(p)) {
        if (p < priceMinCat) return false;
        if (p > priceMaxCat) return false;
      }

      // Disponibilité sur la plage choisie : on exclut les voitures dont une
      // réservation active chevauche [pickupDate, returnDate]. Tant que les
      // périodes ne sont pas chargées (undefined), on n'exclut pas (pas de flicker).
      if (datesActive) {
        const periods = reservedByCar[c._id];
        if (Array.isArray(periods) && periods.length) {
          const overlaps = periods.some((pr) => {
            const ps = new Date(pr.dateDebut);
            const pe = new Date(pr.dateFin);
            return ps <= inEnd && pe >= inStart; // chevauchement de plages
          });
          if (overlaps) return false;
        }
      }

      if (!query) return true;

      const hay = `${c.marque || ""} ${c.modele || ""} ${c.ville || ""} ${c.type_voiture || ""} ${c.transmission || ""} ${c.carburant || ""}`.toLowerCase();
      return hay.includes(query);
    });
  }, [cars, q, filterVille, filterType, onlyDisponible, priceMinCat, priceMaxCat, datesActive, pickupDate, returnDate, reservedByCar]);

  // =========================
  // Car Details modal
  // =========================
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);

  const openDetails = (car) => {
    setSelectedCar(car);
    setDetailsOpen(true);
  };

  // =========================
  // Reservation modal (existing car)  ✅ FIXED
  // =========================
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveCar, setReserveCar] = useState(null);

  const [rDateDebut, setRDateDebut] = useState("");
  const [rDateFin, setRDateFin] = useState("");
  const [rPickup, setRPickup] = useState("");
  const [rNom, setRNom] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPhone, setRPhone] = useState("");
  const [rAdresse, setRAdresse] = useState("");
  const [rAccept, setRAccept] = useState(false);
  const [rLoading, setRLoading] = useState(false);
  const [rPriceInfo, setRPriceInfo] = useState(null); // {total, days, pricePerDay}

  useEffect(() => {
    // prefill
    if (!reserveOpen) return;
    setREmail(currentUser?.email || rEmail || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reserveOpen, currentUser]);

  const resetReservationModal = () => {
    setRDateDebut("");
    setRDateFin("");
    setRPickup("");
    setRNom("");
    setREmail(currentUser?.email || "");
    setRPhone("");
    setRAdresse("");
    setRAccept(false);
    setRPriceInfo(null);
    setRLoading(false);
  };

  const openReserve = (car) => {
    setReserveCar(car);
    resetReservationModal();
    setReserveOpen(true);
  };

  const validateReservation = () => {
    if (!reserveCar?._id) {
      toast.error(T("vCarMissing", "Voiture invalide."));
      return false;
    }

    if (!rDateDebut) {
      toast.error(T("vStartDate", "Veuillez choisir la date de début."));
      return false;
    }
    if (!rDateFin) {
      toast.error(T("vEndDate", "Veuillez choisir la date de retour."));
      return false;
    }

    const a = new Date(rDateDebut);
    const b = new Date(rDateFin);
    if (a >= b) {
      toast.error(T("vEndAfterStart", "La date de retour doit être après la date de début."));
      return false;
    }

    if (!rPickup.trim()) {
      toast.error(T("vPickupLocation", "Veuillez saisir le lieu de prise en charge."));
      return false;
    }

    if (!rNom.trim()) {
      toast.error(T("vFullName", "Veuillez saisir votre nom complet."));
      return false;
    }
    if (!String(currentUser?.email || rEmail || "").trim()) {
      toast.error(T("vEmail", "Veuillez saisir votre email."));
      return false;
    }
    if (!rPhone.trim()) {
      toast.error(T("vPhone", "Veuillez saisir votre téléphone."));
      return false;
    }
    if (!rAdresse.trim()) {
      toast.error(T("vAddress", "Veuillez saisir votre adresse."));
      return false;
    }

    if (!rAccept) {
      toast.error(T("vAcceptCgv", "Veuillez accepter les conditions de paiement (CMI/CGV)."));
      return false;
    }

    return true;
  };

  const computePrice = async () => {
    if (!reserveCar?._id) return;
    if (!rDateDebut || !rDateFin) return;

    try {
      const res = await fetch(`${API_BASE}/api/reservations/voitures/calculate-price/${reserveCar._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dateDebut: rDateDebut, dateFin: rDateFin }),
      });
      const data = await res.json();
      if (!data?.success) return;

      // data.data can be any shape; we try common ones
      const d = data.data || {};
      const total = d.prix_total ?? d.total ?? d.amount ?? d.montant ?? d.price ?? null;
      const days = d.nb_jours ?? d.days ?? d.nights ?? null;

      setRPriceInfo({
        total: total != null ? Number(total) : null,
        days: days != null ? Number(days) : null,
        pricePerDay: Number(reserveCar?.prix_jour) || null,
      });
    } catch (e) {
      console.error(e);
      // silent
    }
  };

  useEffect(() => {
    if (!reserveOpen) return;
    computePrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reserveOpen, rDateDebut, rDateFin, reserveCar?._id]);

  const [paymentMode, setPaymentMode] = useState("email"); // "direct" (CMI) ou "email" (demande)
  useEffect(() => {
    getPaymentMode("voiture").then(setPaymentMode).catch(() => {});
  }, []);

  // ✅ FIX: submitReservation utilise r* + reserveCar, et POST /demande
  const submitReservation = async (e) => {
    e?.preventDefault?.();
    if (!validateReservation()) return;

    // const emailFinal = String(currentUser?.email || rEmail || "").trim();

    const payload = {
  pickupLocation: (rPickup || reserveCar?.ville || "").trim(),
  dateDebut: rDateDebut,
  dateFin: rDateFin,
  nbPersonnes: 1,

  // ✅ CATALOG mode => voiture choisie
  requestedCarId: reserveCar?._id,

  preferences: {
    carType: reserveCar?.type_voiture || null,
    transmission: reserveCar?.transmission || null,
    fuelType: reserveCar?.carburant || null,
  },

  budgetParJour: {
    min: Number(reserveCar?.prix_jour || 0),
    max: Number(reserveCar?.prix_jour || 0),
  },

  souhaiteReduction: false,

  comments: `Demande depuis le catalogue (voiture choisie).
- voitureId: ${reserveCar?._id}
- marque: ${reserveCar?.marque || ""}
- modèle: ${reserveCar?.modele || ""}
- ville: ${reserveCar?.ville || ""}
- prix/jour: ${reserveCar?.prix_jour || ""} MAD
Merci de confirmer la disponibilité et de m'envoyer le lien de paiement.`,

  client: {
    nomComplet: rNom.trim(),
    email: String(currentUser?.email || rEmail || "").trim(),
    telephone: rPhone.trim(),
    adresse: rAdresse.trim(),
  },

  acceptCgv: !!rAccept,
  statut: "EN_ATTENTE",
  compteId: currentUser?._id || null,
  lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
  serviceType: "VOITURE_LOCATION",
};


    try {
      setRLoading(true);

      // ====== MODE "DIRECT" : paiement CMI immédiat (prix calculé côté serveur) ======
      if (paymentMode === "direct") {
        const cmiPayload = {
          voitureId: reserveCar?._id,
          dateDebut: rDateDebut,
          dateFin: rDateFin,
          utilisateur: rNom.trim() || String(currentUser?.email || rEmail || "").trim(),
          email: String(currentUser?.email || rEmail || "").trim(),
          fullName: rNom.trim(),
          phone: rPhone.trim(),
          address: rAdresse.trim(),
          commentaires: "",
          lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
        };
        toast.loading(T("redirectingToPayment", "Redirection vers le paiement sécurisé..."), { id: "cmi-voiture" });
        const html = await cmiService.createVoiturePayment(cmiPayload);
        toast.success(T("redirectingToPayment", "Redirection vers le paiement sécurisé..."), { id: "cmi-voiture", duration: 1500 });
        setReserveOpen(false);
        setReserveCar(null);
        setTimeout(() => {
          document.open();
          document.write(html);
          document.close();
        }, 500);
        return;
      }

      // ====== MODE "EMAIL" : demande, lien envoyé par l'admin ======
      const res = await carRentalService.createRequest(payload);

      if (res?.success === false) {
        toast.error(res?.message || T("sendError", "Erreur lors de l’envoi. Veuillez réessayer."));
        return;
      }

      toast.success(res?.message || T("locationSent", "Votre demande de location a été envoyée !"));
      setReserveOpen(false);
      setReserveCar(null);
      resetReservationModal();
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        T("sendError", "Erreur lors de l’envoi. Veuillez réessayer.");
      console.error("DEMANDE CATALOG LOCATION ERROR:", err?.response?.data || err);
      toast.error(apiMsg);
    } finally {
      setRLoading(false);
    }
  };

  // =========================
  // UI
  // =========================
  // ===== Wizard (demande de location) =====
  const FORM_STEPS = [
    { label: T("wizCar", "Véhicule"), icon: MapPin },
    { label: T("wizInfos", "Vos infos"), icon: User },
    { label: T("wizPayment", "Paiement"), icon: Wallet },
  ];
  const lastFormStep = FORM_STEPS.length - 1;

  const validateFormStep = (s) => {
    if (s === 0) {
      if (!pickupLocation.trim()) return T("vPickupLocation", "Veuillez saisir le lieu de prise en charge.");
      if (!pickupDate) return T("vStartDate", "Veuillez choisir la date de début.");
      if (!returnDate) return T("vEndDate", "Veuillez choisir la date de retour.");
      if (new Date(pickupDate) >= new Date(returnDate))
        return T("vEndAfterStart", "La date de retour doit être après la date de début.");
    }
    if (s === 1) {
      if (!nomComplet.trim()) return T("vFullName", "Veuillez saisir votre nom complet.");
      if (!email.trim()) return T("vEmail", "Veuillez saisir votre email.");
      if (!telephone.trim()) return T("vPhone", "Veuillez saisir votre téléphone.");
      if (!adresse.trim()) return T("vAddress", "Veuillez saisir votre adresse.");
    }
    return null;
  };
  const formNext = () => {
    const err = validateFormStep(formStep);
    if (err) { toast.error(err); return; }
    setFormStep((s) => Math.min(lastFormStep, s + 1));
  };
  const formBack = () => setFormStep((s) => Math.max(0, s - 1));

  return (
    <div
      className="nouzha-premium-skin py-24 min-h-screen relative overflow-hidden"
      style={{ background: "linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)" }}
    >
      {/* Pattern zellij */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${zelijPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "400px",
          backgroundAttachment: "fixed",
          opacity: 0.4,
        }}
      />
      <div className="absolute inset-0 bg-white/80 z-0 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-orange-700 mb-4 drop-shadow-lg">
            {T("carRentalTitle", "Location de voiture")}
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            {T(
              "carRentalSubtitleNew",
              "Choisissez soit une demande sur mesure (formulaire), soit une voiture disponible (catalogue) avec réservation."
            )}
          </p>

          {/* Mode buttons */}
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setMode("form")}
              className={`px-7 py-3 rounded-2xl text-sm font-black uppercase tracking-[0.1em] transition-all shadow-sm flex items-center gap-2 ${
                mode === "form"
                  ? "bg-[#0f172a] text-white shadow-xl"
                  : "bg-white/70 text-gray-500 hover:bg-white hover:text-orange-600"
              }`}
            >
              <MessageSquare className="w-5 h-5" />
              {T("modeForm", "Remplir le formulaire")}
            </button>

            <button
              type="button"
              onClick={() => setMode("catalog")}
              className={`px-7 py-3 rounded-2xl text-sm font-black uppercase tracking-[0.1em] transition-all shadow-sm flex items-center gap-2 ${
                mode === "catalog"
                  ? "bg-[#0f172a] text-white shadow-xl"
                  : "bg-white/70 text-gray-500 hover:bg-white hover:text-orange-600"
              }`}
            >
              <Car className="w-5 h-5" />
              {T("modeCatalog", "Choisir une voiture")}
            </button>
          </div>
        </motion.div>

        {/* =========================
            MODE: FORM
        ========================= */}
        {mode === "form" && (
          <>
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white/90 rounded-3xl shadow-xl p-8 text-center max-w-2xl mx-auto"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send className="w-10 h-10 text-green-600" />
                </div>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {T("successTitle", "Demande envoyée avec succès !")}
                </h2>
                <p className="text-gray-600 mb-6">
                  {T(
                    "successBody",
                    "Nous avons bien reçu votre demande de location. Un agent va l’étudier et vous contactera par email avec une proposition et un lien de paiement sécurisé."
                  )}
                </p>

                <button
                  onClick={() => setSuccess(false)}
                  className="px-6 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all font-semibold"
                >
                  {T("successNewRequest", "Faire une autre demande")}
                </button>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.45, delay: 0.12 }}
                onSubmit={handleSubmitDemande}
                className=""
              >
                {/* ===== Stepper ===== */}
                <div className="flex items-center justify-between gap-2 mb-8 px-2">
                  {FORM_STEPS.map((st, i) => {
                    const Icon = st.icon;
                    const done = i < formStep;
                    const active = i === formStep;
                    return (
                      <React.Fragment key={st.label}>
                        <div className="flex flex-col items-center text-center">
                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm transition-all duration-300 ${active ? "bg-orange-600 text-white scale-110" : done ? "bg-green-500 text-white" : "bg-white text-gray-400 border border-gray-200"}`}>
                            {done ? <CheckCircle2 className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                          </div>
                          <span className={`mt-2 text-[11px] md:text-xs font-bold uppercase tracking-wide ${active ? "text-orange-700" : "text-gray-400"}`}>{st.label}</span>
                        </div>
                        {i < FORM_STEPS.length - 1 && (
                          <div className={`h-1 flex-1 rounded-full mb-6 transition-all duration-300 ${i < formStep ? "bg-green-500" : "bg-gray-200"}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>

                {formStep === 0 && (
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <Car className="w-4 h-4" />
                    </div>
                    <h2 className="text-2xl md:text-[1.9rem] leading-tight font-black uppercase tracking-tight text-[#111827]">
                      {T("rentalInfo", "Informations de location")}
                    </h2>
                  </div>

                  {/* Dictée vocale + Autofill (IA) */}
                  <VoiceDictation
                    stt={stt}
                    onAutofill={handleGeminiAutofill}
                    placeholder={T("locationTranscriptPlaceholder", "La transcription s’affiche ici...")}
                    hint={T(
                      "locationVoiceHint",
                      "Astuce : dites « prise en charge à …, début …, retour …, type SUV …, automatique …, diesel …, budget … »."
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Lieu */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">
                      {T("pickupLocation", "Lieu de prise en charge")} *
                    </label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <MapPin className="mr-2 h-5 w-5 text-orange-500" />
                      <input
                        type="text"
                        value={pickupLocation}
                        onChange={(e) => setPickupLocation(e.target.value)}
                        placeholder={T("pickupLocationPh", "Ex: Aéroport Fès, Centre-ville...")}
                        className="w-full outline-none bg-transparent"
                        required
                      />
                    </div>
                  </div>

                  {/* Nb personnes */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">
                      {T("peopleCount", "Nombre de personnes")} *
                    </label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <Users className="mr-2 h-5 w-5 text-orange-500" />
                      <select
                        value={nbPersonnes}
                        onChange={(e) => setNbPersonnes(e.target.value)}
                        className="w-full outline-none bg-transparent"
                        required
                      >
                        {[...Array(10)].map((_, i) => (
                          <option key={i} value={i + 1}>
                            {language === "en"
                              ? `${i + 1} ${i === 0 ? "person" : "people"}`
                              : `${i + 1} personne${i > 0 ? "s" : ""}`}
                          </option>
                        ))}
                        <option value="11">{language === "en" ? "11+ people" : "11+ personnes"}</option>
                      </select>
                    </div>
                  </div>

                  {/* Date début */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("startDate", "Date de début")} *</label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <CalendarIcon className="mr-2 h-5 w-5 text-orange-500" />
                      <input
                        type="date"
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                        min={today}
                        className="w-full outline-none bg-transparent"
                        required
                      />
                    </div>
                  </div>

                  {/* Date retour */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("endDate", "Date de retour")} *</label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <CalendarIcon className="mr-2 h-5 w-5 text-orange-500" />
                      <input
                        type="date"
                        value={returnDate}
                        onChange={(e) => setReturnDate(e.target.value)}
                        min={pickupDate || today}
                        className="w-full outline-none bg-transparent"
                        required
                      />
                    </div>
                  </div>

                  {/* Type voiture */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("carType", "Type de voiture")}</label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <Car className="mr-2 h-5 w-5 text-orange-500" />
                      <select value={carType} onChange={(e) => setCarType(e.target.value)} className="w-full outline-none bg-transparent">
                        <option value="">{T("any", "Peu importe")}</option>
                        <option value="economy">{T("carEconomy", "Économique")}</option>
                        <option value="suv">{T("carSuv", "SUV")}</option>
                        <option value="luxury">{T("carLuxury", "Luxe")}</option>
                        <option value="van">{T("carVan", "Van / Minibus")}</option>
                      </select>
                    </div>
                  </div>

                  {/* Transmission */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("transmission", "Transmission")}</label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <Settings className="mr-2 h-5 w-5 text-orange-500" />
                      <select value={transmission} onChange={(e) => setTransmission(e.target.value)} className="w-full outline-none bg-transparent">
                        <option value="">{T("any", "Peu importe")}</option>
                        <option value="automatic">{T("auto", "Automatique")}</option>
                        <option value="manual">{T("manual", "Manuelle")}</option>
                      </select>
                    </div>
                  </div>

                  {/* Carburant */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("fuel", "Carburant")}</label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <Fuel className="mr-2 h-5 w-5 text-orange-500" />
                      <select value={fuelType} onChange={(e) => setFuelType(e.target.value)} className="w-full outline-none bg-transparent">
                        <option value="">{T("any", "Peu importe")}</option>
                        <option value="diesel">{T("diesel", "Diesel")}</option>
                        <option value="essence">{T("gasoline", "Essence")}</option>
                        <option value="hybrid">{T("hybrid", "Hybride")}</option>
                        <option value="electric">{T("electric", "Électrique")}</option>
                      </select>
                    </div>
                  </div>

                  {/* Budget / jour */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("budgetPerDay", "Budget par jour (MAD)")} *</label>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-16">{T("budgetMin", "Min")}</span>
                        <input
                          type="number"
                          min="0"
                          value={prixMin}
                          onChange={(e) => setPrixMin(Number(e.target.value))}
                          className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-16">{T("budgetMax", "Max")}</span>
                        <input
                          type="number"
                          min="0"
                          value={prixMax}
                          onChange={(e) => setPrixMax(Number(e.target.value))}
                          className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{T("budgetHint", "Indique un intervalle réaliste pour accélérer le traitement.")}</p>
                  </div>
                  </div>
                </section>
                )}

                {formStep === 1 && (
                <>
                {/* Préférences */}
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <BadgePercent className="w-4 h-4" />
                    </div>
                    <h3 className="text-2xl md:text-[1.65rem] leading-tight font-black uppercase tracking-tight text-[#111827]">{T("rentalPreferences", "Détails / préférences")}</h3>
                  </div>

                  <div className="mt-4 flex items-center gap-3 p-4 bg-white border border-orange-200 rounded-2xl">
                    <input
                      type="checkbox"
                      checked={souhaiteReduction}
                      onChange={(e) => setSouhaiteReduction(e.target.checked)}
                      className="h-4 w-4 accent-orange-600"
                    />
                    <BadgePercent className="w-5 h-5 text-orange-600" />
                    <span className="text-sm text-gray-800">{T("discountWish", "Je souhaite recevoir une offre avec réduction si disponible")}</span>
                  </div>

                  <div className="mt-4">
                    <label className="block font-medium mb-2 text-gray-700">{T("commentsLabel", "Commentaires / besoins spécifiques")}</label>
                    <div className="flex items-start border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <MessageSquare className="mr-2 h-5 w-5 text-orange-500 mt-1" />
                      <textarea
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder={T("commentsPh", "Ex: siège bébé, livraison à l’hôtel, boîte auto obligatoire...")}
                        className="w-full outline-none bg-transparent min-h-[110px]"
                      />
                    </div>
                  </div>
                </section>

                {/* Infos personnelles */}
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <User className="w-4 h-4" />
                    </div>
                    <h2 className="text-2xl md:text-[1.65rem] leading-tight font-black uppercase tracking-tight text-[#111827]">{T("personalInfo", "Informations personnelles")}</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium mb-2 text-gray-700">{T("fullName", "Nom complet")} *</label>
                      <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                        <User className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="text"
                          value={nomComplet}
                          onChange={(e) => setNomComplet(e.target.value)}
                          className="w-full outline-none bg-transparent"
                          placeholder={T("fullNamePh", "Ex: Nom complet")}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium mb-2 text-gray-700">
                        {currentUser ? T("emailAccount", "Email (compte)") : `${T("email", "Email")} *`}
                      </label>
                      <div
                        className={`flex items-center border-2 rounded-xl px-4 py-3 ${
                          currentUser
                            ? "bg-gray-100 border-gray-200"
                            : "bg-white/70 border-orange-200 focus-within:ring-2 focus-within:ring-orange-400 focus-within:border-orange-400"
                        }`}
                      >
                        <Mail className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className={`w-full outline-none bg-transparent ${currentUser ? "cursor-not-allowed text-gray-600" : ""}`}
                          placeholder={T("emailPh", "vous@example.com")}
                          readOnly={!!currentUser}
                          disabled={!!currentUser}
                          required
                        />
                      </div>

                      {currentUser && (
                        <p className="mt-1 text-sm text-gray-500">{T("emailLockedHint", "Cette adresse est liée à votre compte et ne peut pas être modifiée ici.")}</p>
                      )}
                    </div>

                    <div>
                      <label className="block font-medium mb-2 text-gray-700">{T("phone", "Téléphone")} *</label>
                      <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                        <Phone className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="tel"
                          value={telephone}
                          onChange={(e) => setTelephone(e.target.value)}
                          className="w-full outline-none bg-transparent"
                          placeholder={T("phonePh", "Ex: +212 6xx xx xx xx")}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium mb-2 text-gray-700">{T("address", "Adresse")} *</label>
                      <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                        <Home className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="text"
                          value={adresse}
                          onChange={(e) => setAdresse(e.target.value)}
                          className="w-full outline-none bg-transparent"
                          placeholder={T("addressPh", "Ex: Quartier, rue, ville...")}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </section>
                </>
                )}

                {formStep === 2 && (
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <Wallet className="w-4 h-4" />
                    </div>
                    <h3 className="text-2xl md:text-[1.65rem] leading-tight font-black uppercase tracking-tight text-[#111827]">{T("paymentConditions", "Conditions & paiement")}</h3>
                  </div>

                  <div className="p-4 bg-white border border-orange-200 rounded-2xl">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptCgv}
                      onChange={(e) => setAcceptCgv(e.target.checked)}
                      className="mt-1 h-4 w-4 accent-orange-600"
                    />
                    <span className="text-sm text-gray-800">
                      {T("cgvTextPrefix", "J’ai lu et j’accepte les")}{" "}
                      <a href="/cgv" target="_blank" rel="noreferrer" className="text-orange-600 font-semibold hover:underline">
                        {T("cgvLinkText", "Conditions Générales de Vente")}
                      </a>{" "}
                      {T("cgvTextSuffix", "ainsi que les conditions de paiement via CMI.")}
                    </span>
                  </label>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500">{T("securePaymentLabel", "Paiement sécurisé :")}</span>
                    <a
                      href="/paiement-securise"
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-orange-600 font-semibold hover:underline"
                    >
                      {T("securePaymentLink", "Voir les informations CMI / Visa / MasterCard / 3DS")}
                    </a>
                  </div>
                  </div>
                </section>
                )}

                {/* ===== Navigation wizard ===== */}
                <div className="flex items-center justify-between gap-4">
                  {formStep > 0 ? (
                    <button type="button" onClick={formBack} className="inline-flex items-center gap-2 px-7 py-4 rounded-xl font-black uppercase tracking-widest text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm transition-all">
                      ← {T("wizBack", "Précédent")}
                    </button>
                  ) : (
                    <span />
                  )}

                  {formStep < lastFormStep ? (
                    <button type="button" onClick={formNext} className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-black uppercase tracking-widest text-sm shadow-lg hover:from-orange-600 hover:to-red-700 transition-all">
                      {T("wizNext", "Suivant")} →
                    </button>
                  ) : (
                    <button type="submit" disabled={loading || !acceptCgv} className={`inline-flex items-center gap-3 px-10 py-4 rounded-xl font-black uppercase tracking-widest text-sm shadow-lg transition-all ${loading || !acceptCgv ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700"}`}>
                      {loading ? (<><div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin" />{T("sending", "Envoi en cours...")}</>) : (<><Send className="w-5 h-5" />{T("submitRequest", "Soumettre ma demande")}</>)}
                    </button>
                  )}
                </div>

                {formStep === lastFormStep && !acceptCgv && (
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    {T("mustAcceptCgv", "Pour envoyer la demande, vous devez d’abord accepter les conditions (CMI/CGV).")}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-3 max-w-2xl mx-auto text-center">
                  {T("afterSubmitHint", "Après soumission, un agent traitera votre demande, sélectionnera le véhicule le plus adapté, fixera le tarif final et vous enverra un email avec un lien de paiement sécurisé.")}
                </p>

                <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-500">
                  <Wallet className="w-4 h-4" />
                  {T("cmiFooter", "Paiement final via lien sécurisé CMI (3DS)")}
                </div>
              </motion.form>
            )}
          </>
        )}

        {/* =========================
            MODE: CATALOG
        ========================= */}
        {mode === "catalog" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="nouzha-catalog-card bg-white/90 rounded-3xl shadow-xl p-6 md:p-8"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
              <div>
                <h2 className="text-2xl font-bold text-orange-700">{T("catalogTitle", "Choisir une voiture")}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {T("catalogSubtitle", "Parcourez les voitures disponibles, consultez les détails, puis réservez.")}
                </p>
              </div>

              <button
                type="button"
                onClick={fetchCars}
                className="px-4 py-2 rounded-xl border border-orange-200 bg-white hover:bg-orange-50 transition font-semibold text-gray-800"
              >
                {carsLoading ? T("loading", "Chargement...") : T("refresh", "Rafraîchir")}
              </button>
            </div>

            {/* Filters */}
            <div className="mb-6 p-4 rounded-2xl border border-orange-200 bg-white/70">
              <div className="flex items-center gap-2 text-gray-800 font-semibold mb-3">
                <Filter className="w-4 h-4 text-orange-600" />
                {T("filters", "Filtres")}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {/* Search */}
                <div className="md:col-span-2">
                  <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/80">
                    <Search className="mr-2 h-5 w-5 text-orange-500" />
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder={T("searchCarsPh", "Rechercher (marque, modèle, ville...)")}
                      className="w-full outline-none bg-transparent"
                    />
                  </div>
                </div>

                {/* Ville */}
                <div>
                  <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/80">
                    <MapPin className="mr-2 h-5 w-5 text-orange-500" />
                    <select
                      value={filterVille}
                      onChange={(e) => setFilterVille(e.target.value)}
                      className="w-full outline-none bg-transparent"
                    >
                      <option value="">{T("allCities", "Toutes les villes")}</option>
                      {villes.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Type */}
                <div>
                  <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/80">
                    <Car className="mr-2 h-5 w-5 text-orange-500" />
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="w-full outline-none bg-transparent"
                    >
                      <option value="">{T("allTypes", "Tous les types")}</option>
                      {typesVoiture.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                {/* Only disponible */}
                <label className="flex items-center gap-3 p-3 rounded-2xl border border-orange-200 bg-white/80">
                  <input
                    type="checkbox"
                    checked={onlyDisponible}
                    onChange={(e) => setOnlyDisponible(e.target.checked)}
                    className="h-4 w-4 accent-orange-600"
                  />
                  <span className="text-sm text-gray-800">{T("onlyAvailable", "Afficher uniquement disponibles")}</span>
                </label>

                {/* Price min/max */}
                <div className="md:col-span-2 p-3 rounded-2xl border border-orange-200 bg-white/80">
                  <div className="text-sm font-semibold text-gray-800 mb-2">{T("pricePerDayFilter", "Prix / jour")}</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">{T("budgetMin", "Min")}</div>
                      <input
                        type="number"
                        min="0"
                        value={priceMinCat}
                        onChange={(e) => setPriceMinCat(clamp(Number(e.target.value), 0, priceMaxCat))}
                        className="w-full border-2 border-orange-200 rounded-xl px-4 py-2 bg-white/70 outline-none"
                      />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">{T("budgetMax", "Max")}</div>
                      <input
                        type="number"
                        min="0"
                        value={priceMaxCat}
                        onChange={(e) => setPriceMaxCat(Math.max(Number(e.target.value), priceMinCat))}
                        className="w-full border-2 border-orange-200 rounded-xl px-4 py-2 bg-white/70 outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between flex-wrap gap-2 text-xs text-gray-600">
                <span className="flex items-center gap-2 flex-wrap">
                  {T("resultsCount", "Résultats")} : <b className="text-gray-800">{filteredCars.length}</b>
                  {datesActive && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-700 font-semibold px-2.5 py-1">
                      <CalendarIcon className="w-3.5 h-3.5" />
                      {T("availableForDates", "Disponibles pour")} {pickupDate} → {returnDate}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setQ("");
                    setFilterVille("");
                    setFilterType("");
                    setOnlyDisponible(true);
                  }}
                  className="text-orange-700 font-semibold hover:underline"
                >
                  {T("resetFilters", "Réinitialiser")}
                </button>
              </div>
            </div>

            {/* Content */}
            {carsError && (
              <div className="mb-4 p-4 rounded-2xl border border-red-200 bg-red-50 text-red-700 text-sm">{carsError}</div>
            )}

            {carsLoading ? (
              <div className="py-10 text-center text-gray-600">
                <div className="mx-auto w-8 h-8 border-t-2 border-b-2 border-orange-600 rounded-full animate-spin mb-3" />
                {T("loadingCars", "Chargement des voitures...")}
              </div>
            ) : filteredCars.length === 0 ? (
              <div className="py-10 text-center text-gray-600">{T("noCars", "Aucune voiture ne correspond aux filtres.")}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCars.map((c) => {
                  const img = safeImg(c?.image);
                  const dispo = c?.disponible !== false;

                  return (
                    <motion.div
                      key={c?._id || `${c?.marque}-${c?.modele}-${Math.random()}`}
                      whileHover={{ y: -3 }}
                      className="rounded-3xl overflow-hidden bg-white border border-orange-100 shadow-lg"
                    >
                      <div className="relative h-48 bg-gray-100">
                        {img ? (
                          <img
                            src={img}
                            alt={`${c?.marque || ""} ${c?.modele || ""}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <Car className="w-10 h-10" />
                          </div>
                        )}

                        <div className="absolute top-3 left-3 flex gap-2">
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/90 border border-orange-100 text-orange-700">
                            {formatMAD(c?.prix_jour)} / {T("day", "jour")}
                          </span>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-bold border ${
                              dispo ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"
                            }`}
                          >
                            {dispo ? T("available", "Disponible") : T("unavailable", "Indisponible")}
                          </span>
                        </div>
                      </div>

                      <div className="p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-lg font-extrabold text-gray-900">
                              {(c?.marque || "").toUpperCase()}{" "}
                              <span className="text-gray-800">{c?.modele || ""}</span>
                            </div>
                            <div className="mt-1 text-sm text-gray-600 flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-orange-600" />
                              <span>{c?.ville || "-"}</span>
                            </div>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-orange-600" />
                            <span>
                              {c?.nb_places || "-"} {T("seats", "places")}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4 text-orange-600" />
                            <span>{c?.transmission || "-"}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Fuel className="w-4 h-4 text-orange-600" />
                            <span>{c?.carburant || "-"}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Car className="w-4 h-4 text-orange-600" />
                            <span>{c?.type_voiture || "-"}</span>
                          </div>
                        </div>

                        <div className="mt-4 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openDetails(c)}
                            className="flex-1 px-4 py-2 rounded-xl border border-orange-200 bg-white hover:bg-orange-50 transition font-semibold text-gray-800 flex items-center justify-center gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            {T("viewDetails", "Détails")}
                          </button>

                          <button
                            type="button"
                            disabled={!dispo}
                            onClick={() => openReserve(c)}
                            className={`flex-1 px-4 py-2 rounded-xl transition font-semibold flex items-center justify-center gap-2 ${
                              dispo
                                ? "bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700"
                                : "bg-gray-200 text-gray-500 cursor-not-allowed"
                            }`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            {T("reserve", "Réserver")}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-500">
              <Wallet className="w-4 h-4" />
              {T("cmiFooter", "Paiement final via lien sécurisé CMI (3DS)")}
            </div>
          </motion.div>
        )}
      </div>

      {/* =========================
          Details Modal
      ========================= */}
      <ModalShell
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={T("carDetailsTitle", "Détails de la voiture")}
        maxWidth="max-w-4xl"
      >
        {!selectedCar ? null : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-2xl overflow-hidden border bg-gray-50">
              {safeImg(selectedCar?.image) ? (
                <img
                  src={safeImg(selectedCar?.image)}
                  alt={`${selectedCar?.marque || ""} ${selectedCar?.modele || ""}`}
                  className="w-full h-64 object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-full h-64 flex items-center justify-center text-gray-400">
                  <Car className="w-12 h-12" />
                </div>
              )}
            </div>

            <div>
              <div className="text-2xl font-extrabold text-gray-900">
                {(selectedCar?.marque || "").toUpperCase()}{" "}
                <span className="text-gray-800">{selectedCar?.modele || ""}</span>
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-orange-50 text-orange-800 border border-orange-200">
                  {formatMAD(selectedCar?.prix_jour)} / {T("day", "jour")}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-white border border-orange-200 text-gray-800">
                  {selectedCar?.ville || "-"}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-white border border-orange-200 text-gray-800">
                  {selectedCar?.type_voiture || "-"}
                </span>
              </div>

              <div className="mt-5 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setDetailsOpen(false);
                    openReserve(selectedCar);
                  }}
                  disabled={selectedCar?.disponible === false}
                  className={`flex-1 px-4 py-3 rounded-xl transition font-bold flex items-center justify-center gap-2 ${
                    selectedCar?.disponible === false
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700"
                  }`}
                >
                  <CheckCircle2 className="w-5 h-5" />
                  {T("reserveThisCar", "Réserver cette voiture")}
                </button>
              </div>
            </div>
          </div>
        )}
      </ModalShell>

      {/* =========================
          Reservation Modal
      ========================= */}
      <ModalShell
        open={reserveOpen}
        onClose={() => setReserveOpen(false)}
        title={T("reserveModalTitle", "Réserver une voiture")}
        maxWidth="max-w-3xl"
      >
        {!reserveCar ? null : (
          <div>
            {/* Car summary */}
            <div className="p-4 rounded-2xl border border-orange-200 bg-orange-50/40 mb-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-lg font-extrabold text-gray-900">
                    {(reserveCar?.marque || "").toUpperCase()}{" "}
                    <span className="text-gray-800">{reserveCar?.modele || ""}</span>
                  </div>
                  <div className="text-sm text-gray-700 flex items-center gap-2 mt-1">
                    <MapPin className="w-4 h-4 text-orange-600" />
                    <span>{reserveCar?.ville || "-"}</span>
                    <span className="text-gray-400">•</span>
                    <span className="font-bold">
                      {formatMAD(reserveCar?.prix_jour)} / {T("day", "jour")}
                    </span>
                  </div>
                </div>

                {rPriceInfo?.total != null && (
                  <div className="px-4 py-2 rounded-2xl bg-white border border-orange-200">
                    <div className="text-xs text-gray-500">{T("estimatedTotal", "Total estimé")}</div>
                    <div className="text-lg font-extrabold text-orange-700">{formatMAD(rPriceInfo.total)}</div>
                    {rPriceInfo?.days != null && (
                      <div className="text-xs text-gray-500">
                        {rPriceInfo.days} {T("days", "jours")} × {formatMAD(rPriceInfo.pricePerDay)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Dates + pickup */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {T("startDate", "Date de début")} *
                </label>
                <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                  <CalendarIcon className="mr-2 h-5 w-5 text-orange-500" />
                  <input
                    type="date"
                    value={rDateDebut}
                    onChange={(e) => setRDateDebut(e.target.value)}
                    min={today}
                    className="w-full outline-none bg-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{T("endDate", "Date de retour")} *</label>
                <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                  <CalendarIcon className="mr-2 h-5 w-5 text-orange-500" />
                  <input
                    type="date"
                    value={rDateFin}
                    onChange={(e) => setRDateFin(e.target.value)}
                    min={rDateDebut || today}
                    className="w-full outline-none bg-transparent"
                    required
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">{T("pickupLocation", "Lieu de prise en charge")} *</label>
                <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                  <MapPin className="mr-2 h-5 w-5 text-orange-500" />
                  <input
                    type="text"
                    value={rPickup}
                    onChange={(e) => setRPickup(e.target.value)}
                    placeholder={T("pickupLocationPh", "Ex: Aéroport Fès, Centre-ville...")}
                    className="w-full outline-none bg-transparent"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Personal info */}
            <div className="mb-6">
              <div className="text-sm font-extrabold text-gray-800 mb-3">{T("personalInfo", "Informations personnelles")}</div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{T("fullName", "Nom complet")} *</label>
                  <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                    <User className="mr-2 h-5 w-5 text-orange-500" />
                    <input
                      type="text"
                      value={rNom}
                      onChange={(e) => setRNom(e.target.value)}
                      className="w-full outline-none bg-transparent"
                      placeholder={T("fullNamePh", "Ex: Nom complet")}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {currentUser ? T("emailAccount", "Email (compte)") : `${T("email", "Email")} *`}
                  </label>
                  <div className={`flex items-center border-2 rounded-xl px-4 py-3 ${currentUser ? "bg-gray-100 border-gray-200" : "bg-white/70 border-orange-200"}`}>
                    <Mail className="mr-2 h-5 w-5 text-orange-500" />
                    <input
                      type="email"
                      value={currentUser ? (currentUser?.email || "") : rEmail}
                      onChange={(e) => setREmail(e.target.value)}
                      className={`w-full outline-none bg-transparent ${currentUser ? "cursor-not-allowed text-gray-600" : ""}`}
                      placeholder={T("emailPh", "vous@example.com")}
                      readOnly={!!currentUser}
                      disabled={!!currentUser}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{T("phone", "Téléphone")} *</label>
                  <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                    <Phone className="mr-2 h-5 w-5 text-orange-500" />
                    <input
                      type="tel"
                      value={rPhone}
                      onChange={(e) => setRPhone(e.target.value)}
                      className="w-full outline-none bg-transparent"
                      placeholder={T("phonePh", "Ex: +212 6xx xx xx xx")}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{T("address", "Adresse")} *</label>
                  <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                    <Home className="mr-2 h-5 w-5 text-orange-500" />
                    <input
                      type="text"
                      value={rAdresse}
                      onChange={(e) => setRAdresse(e.target.value)}
                      className="w-full outline-none bg-transparent"
                      placeholder={T("addressPh", "Ex: Quartier, rue, ville...")}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* CGV */}
            <div className="mb-6 p-4 bg-white border border-orange-200 rounded-2xl">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rAccept}
                  onChange={(e) => setRAccept(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-orange-600"
                />
                <span className="text-sm text-gray-800">
                  {T("cgvTextPrefix", "J’ai lu et j’accepte les")}{" "}
                  <a href="/cgv" target="_blank" rel="noreferrer" className="text-orange-600 font-semibold hover:underline">
                    {T("cgvLinkText", "Conditions Générales de Vente")}
                  </a>{" "}
                  {T("cgvTextSuffix", "ainsi que les conditions de paiement via CMI.")}
                </span>
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setReserveOpen(false)}
                className="px-5 py-3 rounded-xl border border-orange-200 bg-white hover:bg-orange-50 transition font-semibold text-gray-800"
              >
                {T("cancel", "Annuler")}
              </button>

              <button
                type="button"
                disabled={rLoading || !rAccept}
                onClick={submitReservation}
                className={`flex-1 px-6 py-3 rounded-xl transition font-bold shadow-lg flex items-center justify-center gap-2 ${
                  rLoading || !rAccept
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700"
                }`}
              >
                {rLoading ? (
                  <>
                    <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin" />
                    {T("sending", "Envoi en cours...")}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {T("confirmReserve", "Confirmer la réservation")}
                  </>
                )}
              </button>
            </div>

            {!rAccept && (
              <p className="text-xs text-gray-500 mt-3">
                {T("mustAcceptCgv", "Pour envoyer la demande, vous devez d’abord accepter les conditions (CMI/CGV).")}
              </p>
            )}

            <div className="mt-6 flex items-center gap-2 text-xs text-gray-500">
              <Wallet className="w-4 h-4" />
              {T("cmiFooter", "Paiement final via lien sécurisé CMI (3DS)")}
            </div>
          </div>
        )}
      </ModalShell>
    </div>
  );
};

export default Location;
